import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";

const UserContext = createContext({
  user: null,
  userData: null,
  loading: true,
  error: null,
  // Organization data
  orgData: null,
  isOwner: false,
  isOperator: false,
  isClient: false,
  hasConvenioAccess: false,
  // Helper functions for hierarchical relationships (legacy)
  hasDependientes: false,
  hasSuperiores: false,
  isBoss: false,
  isSubordinate: false,
  // User lookup functions
  usersCache: {},
  getUserEmail: () => "Desconocido",
  getUserName: () => "Desconocido",
  getInitials: () => "?",
  orgUsersList: [],
});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usersCache, setUsersCache] = useState({});
  const [orgData, setOrgData] = useState(null);

  // Organization role helpers
  const isOwner = userData?.rol === "owner";
  const isOperator = userData?.rol === "operator";
  const isClient = userData?.rol === "client";
  const hasConvenioAccess = Boolean(userData?.convenioAccess && Object.keys(userData.convenioAccess).length > 0);

  // Calculate hierarchical relationships (legacy - for backward compatibility)
  const hasDependientes = userData?.dependientes && Array.isArray(userData.dependientes) && userData.dependientes.length > 0;
  const hasSuperiores = userData?.superiores && Array.isArray(userData.superiores) && userData.superiores.length > 0;
  const isBoss = hasDependientes;
  const isSubordinate = hasSuperiores;

  // Function to fetch user information for a given UID
  const fetchUserInfo = useCallback(async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, "usuarios", uid));
      if (userDoc.exists()) {
        const userInfo = { id: userDoc.id, ...userDoc.data() };
        setUsersCache(prev => {
          // Only update if the user info has actually changed
          if (prev[uid]?.id === userInfo.id && 
              JSON.stringify(prev[uid]) === JSON.stringify(userInfo)) {
            return prev;
          }
          return { ...prev, [uid]: userInfo };
        });
        return userInfo;
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
    return null;
  }, []); // Remove usersCache dependency

  // Function to get user email by UID
  const getUserEmail = (uid) => {
    if (!uid) return userData?.correo || "Desconocido";
    const userInfo = usersCache[uid];
    return userInfo?.correo || userData?.correo || "Desconocido";
  };

  // Function to get user name by UID. Only falls back to the viewer's own
  // name for their own uid — an uncached uid must never render as the viewer.
  const getUserName = (uid) => {
    if (!uid || uid === user?.uid) return userData?.nombre || "Desconocido";
    return usersCache[uid]?.nombre || "Desconocido";
  };

  // Function to get user initials by UID
  const getInitials = useCallback((uid) => {
    const nombre = usersCache[uid]?.nombre || '';
    if (!nombre) return '?';
    const parts = nombre.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase() || '?';
  }, [usersCache]);

  // Memoized list of org users with full data
  const orgUsersList = useMemo(() => {
    if (!orgData?.usuarios || !Array.isArray(orgData.usuarios)) return [];
    return orgData.usuarios
      .map(uid => usersCache[uid])
      .filter(Boolean);
  }, [orgData?.usuarios, usersCache]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(true);
      setError(null);
      setUserData(null);
      setUsersCache({});
      setOrgData(null);
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  // Real-time listener for user data changes
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribeUser = onSnapshot(
      doc(db, "usuarios", user.uid),
      async (userDoc) => {
        if (userDoc.exists()) {
          const userInfo = { id: userDoc.id, ...userDoc.data() };
          setUserData(userInfo);
          
          // Cache the current user
          setUsersCache(prev => ({ ...prev, [user.uid]: userInfo }));
          
          // Fetch information for all users in the hierarchy
          const allUserIds = new Set();
          
          // Add dependientes
          if (userInfo.dependientes && Array.isArray(userInfo.dependientes)) {
            userInfo.dependientes.forEach(uid => allUserIds.add(uid));
          }
          
          // Add superiores
          if (userInfo.superiores && Array.isArray(userInfo.superiores)) {
            userInfo.superiores.forEach(uid => allUserIds.add(uid));
          }
          
          // Fetch all user information in parallel (using a separate function to avoid dependency issues)
          const fetchAllUsers = async () => {
            const userPromises = Array.from(allUserIds).map(async (uid) => {
              try {
                const userDoc = await getDoc(doc(db, "usuarios", uid));
                if (userDoc.exists()) {
                  const userInfo = { id: userDoc.id, ...userDoc.data() };
                  setUsersCache(prev => {
                    // Only update if the user info has actually changed
                    if (prev[uid]?.id === userInfo.id && 
                        JSON.stringify(prev[uid]) === JSON.stringify(userInfo)) {
                      return prev;
                    }
                    return { ...prev, [uid]: userInfo };
                  });
                }
              } catch (error) {
                console.error("Error fetching user info:", error);
              }
            });
            await Promise.all(userPromises);
          };
          
          fetchAllUsers();
          
        } else {
          setUserData(null);
          setError("No user data found in Firestore.");
        }
      },
      (err) => {
        setError("Error listening to user data changes.");
        console.error("User data listener error:", err);
      }
    );

    return () => unsubscribeUser();
  }, [user?.uid]); // Remove fetchUserInfo dependency

  // Real-time listener for organization data (skip for client users)
  useEffect(() => {
    if (!userData?.org || userData?.rol === 'client') {
      setOrgData(null);
      return;
    }

    const unsubscribeOrg = onSnapshot(
      doc(db, "orgs", userData.org),
      (orgDoc) => {
        if (orgDoc.exists()) {
          setOrgData({ id: orgDoc.id, ...orgDoc.data() });
        } else {
          setOrgData(null);
        }
      },
      (err) => {
        console.error("Organization data listener error:", err);
        setOrgData(null);
      }
    );

    return () => unsubscribeOrg();
  }, [userData?.org, userData?.rol]);

  // Load org users into cache when orgData changes
  useEffect(() => {
    if (!orgData?.usuarios || !Array.isArray(orgData.usuarios)) return;

    const loadOrgUsers = async () => {
      const promises = orgData.usuarios.map(async (uid) => {
        // Skip if already in cache
        if (usersCache[uid]) return;
        try {
          const userDoc = await getDoc(doc(db, "usuarios", uid));
          if (userDoc.exists()) {
            const userInfo = { id: userDoc.id, ...userDoc.data() };
            setUsersCache(prev => ({ ...prev, [uid]: userInfo }));
          }
        } catch (error) {
          console.error("Error fetching org user info:", error);
        }
      });
      await Promise.all(promises);
    };

    loadOrgUsers();
  }, [orgData?.usuarios]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <UserContext.Provider value={{
      user,
      userData,
      loading,
      error,
      // Organization data
      orgData,
      isOwner,
      isOperator,
      isClient,
      hasConvenioAccess,
      // Legacy hierarchical relationships
      hasDependientes,
      hasSuperiores,
      isBoss,
      isSubordinate,
      usersCache,
      getUserEmail,
      getUserName,
      getInitials,
      orgUsersList,
      fetchUserInfo
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
